import React, { useState, useMemo } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

export default function AdminTable({
  columns,
  data = [],
  searchPlaceholder = "Search...",
  searchQuery = "",
  onSearchChange,
  actions,
  pageSize = 10,
  rowKey = "id",
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [internalSearch, setInternalSearch] = useState("");

  const activeSearch = onSearchChange ? searchQuery : internalSearch;

  // Search filter if not handled externally
  const filteredData = useMemo(() => {
    if (onSearchChange || !activeSearch.trim()) return data;
    const term = activeSearch.toLowerCase();
    return data.filter((item) =>
      Object.values(item).some(
        (val) => val && String(val).toLowerCase().includes(term)
      )
    );
  }, [data, activeSearch, onSearchChange]);

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (onSearchChange) {
      onSearchChange(val);
    } else {
      setInternalSearch(val);
    }
    setCurrentPage(1);
  };

  return (
    <div className="admin-table-wrapper">
      <div className="admin-table-toolbar">
        <div className="admin-search-input-wrap">
          <Search size={16} className="admin-search-icon" />
          <input
            type="text"
            className="admin-search-input"
            placeholder={searchPlaceholder}
            value={activeSearch}
            onChange={handleSearchChange}
          />
        </div>

        {actions && <div className="admin-table-actions">{actions}</div>}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.align || "left" }}
                >
                  {col.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}
                >
                  No records found.
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr key={item[rowKey] || index}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ textAlign: col.align || "left" }}
                    >
                      {col.render ? col.render(item[col.key], item, index) : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--admin-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "13px",
            color: "#64748b",
          }}
        >
          <span>
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, filteredData.length)} of{" "}
            {filteredData.length} entries
          </span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              className="admin-btn admin-btn-secondary admin-btn-sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span style={{ display: "flex", alignItems: "center", padding: "0 8px", fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="admin-btn admin-btn-secondary admin-btn-sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
